import { Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { StorageService } from '../context/storageService';
import { isPlatformBrowser } from '@angular/common';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthStore {
  sessionStorageService = inject(StorageService);
  oidcSecurityService = inject(OidcSecurityService);
  platformId = inject(PLATFORM_ID);

  readonly isLoading = signal(true);
  readonly isLoggingOut = signal(false);
  readonly isReady = computed(
    () => this.isInitialized() && !this.isLoading() && !this.isLoggingOut(),
  );

  readonly isAuthenticated = toSignal(this.oidcSecurityService.isAuthenticated$, {
    initialValue: {
      isAuthenticated: false,
      allConfigsAuthenticated: [],
    },
  });

  readonly userData = toSignal(this.oidcSecurityService.userData$, {
    initialValue: null,
  });
  readonly accessToken = toSignal(
    this.oidcSecurityService.isAuthenticated$.pipe(
      switchMap((auth) =>
        auth?.isAuthenticated ? this.oidcSecurityService.getAccessToken() : of(''),
      ),
    ),
    {
      initialValue: '',
    },
  );
  readonly accessToken$ = this.oidcSecurityService.getAccessToken();

  readonly loggedIn = computed(() => this.isAuthenticated().isAuthenticated);

  readonly userClaims = computed(() => this.userData()?.userData ?? []);

  readonly userName = computed(() => this.userData()?.userData?.name ?? '');

  readonly isInitialized = signal(false);

  login(returnUrl?: string) {
    this.oidcSecurityService.authorize();
  }

  logout() {
    this.isLoading.set(true);
    this.isLoggingOut.set(true);
    this.oidcSecurityService.logoffAndRevokeTokens().subscribe({
      next: () => {},
      error: () => {},
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  async init(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('Not running in a browser environment. Skipping authentication initialization.');
      return;
    }
    if (this.isInitialized()) {
      return;
    }
    this.isInitialized.set(true);
    this.isLoading.set(true);
    try {
      const result = await firstValueFrom(this.oidcSecurityService.checkAuth());
    } catch (error) {
      console.error('Error during authentication initialization:', error);
      this.isLoading.set(false);
    } finally {
      this.isLoading.set(false);
    }
  }
}
